/**
 * Uma das seis linhas fixas do prontuário: o item, o estado em palavra, o último registro e as
 * ações que cabem a esse estado. Nenhuma ação que apaga leva a cor primária.
 */
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  ROTULO_DA_METODOLOGIA,
  ROTULO_DO_RESULTADO,
  ROTULO_DO_TIPO_DE_REGISTRO,
  type FlammableItemSummary,
  type FlammableRecord,
  type FlammableRecordType,
} from '../../../../../types/flammableInstallation';
import { ItemStateChip } from '../../FlammableChips';
import { formatarData } from '../../flammableDates';
import { tamanhoEmTexto } from './arquivoDoRegistro';

/** Onde a NR 20 pede cada item (20.6.1 e 20.15.5). */
const ITEM_DA_NORMA: Readonly<Record<FlammableRecordType, string>> = {
  PROJETO: 'item 20.6.1 a',
  PLANO_INSPECAO_MANUTENCAO: 'item 20.6.1 b',
  ANALISE_RISCOS: 'item 20.6.1 c',
  PLANO_PREVENCAO_CONTROLE: 'item 20.6.1 d',
  PLANO_RESPOSTA_EMERGENCIAS: 'item 20.6.1 e',
  SIMULADO: 'item 20.15.5 · pelo menos uma vez por ano',
};

export interface LinhaDoProntuarioProps {
  tipo: FlammableRecordType;
  item: FlammableItemSummary | undefined;
  /** O último registro do tipo; `null` quando não há nenhum. */
  registro: FlammableRecord | null;
  canEdit: boolean;
  /** Há um formulário com dados digitados em outra linha: abrir este perderia o que foi digitado. */
  semAbrirFormulario: boolean;
  /** Já há um arquivo subindo neste item. */
  enviandoArquivo: boolean;
  /** O que deu errado nas ações desta linha (arquivo recusado, download falho). */
  erro: string | null;
  onRegistrar: () => void;
  onNovaVersao: () => void;
  onEditar: () => void;
  onExcluir: () => void;
  onBaixar: () => void;
  onAnexar: (arquivo: File) => void;
  /** O formulário aberto nesta linha, se for o caso. */
  children?: React.ReactNode;
}

const BotaoDeLinha: React.FC<
  Omit<React.ComponentProps<typeof Button>, 'aria-label'> & { texto: string; rotuloAcessivel: string }
> = ({ texto, rotuloAcessivel, ...props }) => (
  <Button aria-label={rotuloAcessivel} {...props} sx={{ minHeight: 44, ...props.sx }}>
    {texto}
  </Button>
);

const ultimoRegistroEmTexto = (registro: FlammableRecord, proximaRevisao: string | null): string => {
  const partes = [formatarData(registro.documentedAt)];
  partes.push(
    registro.responsibleRegistration ? `${registro.responsible} (${registro.responsibleRegistration})` : registro.responsible
  );
  if (registro.methodology) partes.push(ROTULO_DA_METODOLOGIA[registro.methodology]);
  if (registro.result) partes.push(ROTULO_DO_RESULTADO[registro.result]);
  if (proximaRevisao) partes.push(`revisar até ${formatarData(proximaRevisao)}`);
  return partes.join(' · ');
};

export const LinhaDoProntuario: React.FC<LinhaDoProntuarioProps> = ({
  tipo,
  item,
  registro,
  canEdit,
  semAbrirFormulario,
  enviandoArquivo,
  erro,
  onRegistrar,
  onNovaVersao,
  onEditar,
  onExcluir,
  onBaixar,
  onAnexar,
  children,
}) => {
  const rotulo = ROTULO_DO_TIPO_DE_REGISTRO[tipo];
  const estado = item?.state ?? 'AUSENTE';
  const simulado = tipo === 'SIMULADO';
  const temArquivo = Boolean(registro?.originalFileName);
  const proximaRevisao = item?.nextReview ?? registro?.nextReviewDate ?? null;
  const bloqueado = semAbrirFormulario;

  return (
    <Box component="li" sx={{ listStyle: 'none', py: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 2fr) 140px minmax(240px, 3fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box>
          <Typography variant="subtitle1" component="h4" fontWeight={700}>
            {rotulo}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {ITEM_DA_NORMA[tipo]}
          </Typography>
        </Box>
        <Box>
          <ItemStateChip state={estado} />
        </Box>
        <Box>
          {registro ? (
            <>
              <Typography variant="body1">{ultimoRegistroEmTexto(registro, proximaRevisao)}</Typography>
              {registro.location && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                  Guardado em: {registro.location}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                {temArquivo
                  ? `${registro.originalFileName} · ${tamanhoEmTexto(registro.fileSize ?? 0)}`
                  : 'Sem arquivo anexado'}
              </Typography>
            </>
          ) : (
            <Typography variant="body1">
              {estado === 'NAO_EXIGIDO'
                ? 'A norma não pede este documento nesta faixa. Se você registrar, ele aparece no índice do laudo marcado como não exigido.'
                : simulado
                  ? 'Nenhum simulado registrado. A norma pede pelo menos um por ano.'
                  : 'Nenhum registro. A norma exige este documento para esta instalação.'}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {registro && temArquivo && (
          <BotaoDeLinha texto="Baixar" rotuloAcessivel={`Baixar ${rotulo}`} variant="text" onClick={onBaixar} />
        )}
        {canEdit && registro && !temArquivo && !simulado && (
          <Button
            component="label"
            variant="text"
            startIcon={<UploadFileIcon />}
            disabled={enviandoArquivo}
            sx={{ minHeight: 44 }}
          >
            Anexar arquivo
            <input
              hidden
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              aria-label={`Arquivo da ${rotulo} (PDF, PNG ou JPEG, até 9 MB)`}
              onChange={(event) => {
                const escolhido = event.target.files?.[0] ?? null;
                event.target.value = '';
                if (escolhido) onAnexar(escolhido);
              }}
            />
          </Button>
        )}
        {canEdit && registro && (
          <>
            <BotaoDeLinha texto="Editar" rotuloAcessivel={`Editar ${rotulo}`} variant="text" disabled={bloqueado} onClick={onEditar} />
            <BotaoDeLinha
              texto="Excluir"
              rotuloAcessivel={`Excluir ${rotulo}`}
              variant="text"
              color="error"
              onClick={onExcluir}
            />
            <BotaoDeLinha
              texto={simulado ? 'Registrar novo simulado' : 'Nova versão'}
              rotuloAcessivel={simulado ? 'Registrar novo simulado' : `Nova versão de ${rotulo}`}
              variant="outlined"
              disabled={bloqueado}
              onClick={onNovaVersao}
            />
          </>
        )}
        {canEdit && !registro && (
          <BotaoDeLinha
            texto={estado === 'NAO_EXIGIDO' ? 'Registrar mesmo assim' : 'Registrar'}
            rotuloAcessivel={`${estado === 'NAO_EXIGIDO' ? 'Registrar mesmo assim' : 'Registrar'} ${rotulo}`}
            variant="outlined"
            disabled={bloqueado}
            onClick={onRegistrar}
          />
        )}
      </Box>
      {bloqueado && canEdit && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '1rem' }}>
          Guarde ou cancele o formulário aberto para mexer neste item.
        </Typography>
      )}
      {erro && (
        <Typography variant="body2" color="error" role="alert" sx={{ mt: 1 }}>
          {erro}
        </Typography>
      )}
      {children}
    </Box>
  );
};

export default LinhaDoProntuario;
